import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/SkyFxt.css";
import { db } from "../lib/firebase";
import { doc, getDoc } from "firebase/firestore";
import Loader from "../components/Loader";
import MarketMarquee from "../components/stocks/MarketMarque";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserCog,
  faArrowDown,
  faArrowUp,
  faExchangeAlt,
  faHistory,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";

const MegaFXT = ({ user }) => {
  const navigate = useNavigate();
  const [accountBalances, setAccountBalances] = useState({});
  const [userData, setUserData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recommendedPlan, setRecommendedPlan] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [userDoc, plansDoc] = await Promise.all([
          getDoc(doc(db, "users", user.uid)),
          getDoc(doc(db, "users", "plans"))
        ]);
        
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData(data);

          const allTransactions = data.transactions || [];
          setTransactions(allTransactions);

          const balances = {};

          allTransactions.forEach((txn) => {
            if (!balances[txn.account]) {
              balances[txn.account] = 0;
            }
            if (txn.type === "credit") {
              balances[txn.account] += txn.amount;
            } else if (txn.type === "debit") {
              balances[txn.account] -= txn.amount;
            }
          });

          setAccountBalances(balances);
        }

        if (plansDoc.exists()) {
          const plansData = plansDoc.data();
          const plansArray = Object.entries(plansData).map(([key, plan]) => ({
            key,
            ...plan
          }));
          if (plansArray.length > 0) {
            const randomPlan = plansArray[Math.floor(Math.random() * plansArray.length)];
            setRecommendedPlan(randomPlan);
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (user?.uid) {
      fetchData();
    }
  }, [user]);

  const totalInvestmentBalance =
    (accountBalances.Investments || 0) + (accountBalances.Trading || 0);
  const tradingProfit = userData?.activeTrade 
    ? (Number(userData.activeTrade.amount) || 0) + (Number(userData.activeTrade.earnings) || 0) + (Number(userData.activeTrade.profit) || 0)
    : 0;
  const refCount = transactions.filter(
    (txn) => txn.account === "Investments" && txn.beneficiary === "ref",
  ).length;

  
  const investmentByBeneficiary = {};
  transactions
    .filter((txn) => txn.account === "Investments")
    .forEach((txn) => {
      const ben = txn.beneficiary || "Other";
      if (!investmentByBeneficiary[ben]) {
        investmentByBeneficiary[ben] = 0;
      }
      if (txn.type === "credit") {
        investmentByBeneficiary[ben] += txn.amount;
      } else if (txn.type === "debit") {
        investmentByBeneficiary[ben] -= txn.amount;
      }
    });

  const beneficiaryBalances = Object.entries(investmentByBeneficiary).map(
    ([name, amount]) => ({
      name,
      amount,
    }),
  );

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="skyfxt-page">
      <MarketMarquee />

      {/* Header balance section */}
      <div className="skyfxt-box">
        <div className="skyfxt-main">
          <div className="skyfxt-header">
            <div className="skyfxt-balance">
              <span className="balance-label">Total Investment Balance</span>
              <span className="label-amount">
                $
                {totalInvestmentBalance.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="skyfxt-user">
              <span className="user-name">{userData?.fullName || "User"}</span>
              <FontAwesomeIcon icon={faUserCog} className="user-icon" />
            </div>
          </div>

          <div className="skyfxt-actions">
            <div
              className="action-item"
              onClick={() => navigate("/fxt/deposit")}
            >
              <FontAwesomeIcon icon={faArrowDown} />
              <span>Deposit</span>
            </div>
            <div
              className="action-item"
              onClick={() => navigate("/fxt/withdraw")}
            >
              <FontAwesomeIcon icon={faArrowUp} />
              <span>Withdraw</span>
            </div>
            <div className="action-item" onClick={() => navigate("/fxt/trade")}>
              <FontAwesomeIcon icon={faExchangeAlt} />
              <span>Trade</span>
            </div>
            <div
              className="action-item"
              onClick={() => navigate("/fxt/transactions")}
            >
              <FontAwesomeIcon icon={faHistory} />
              <span>History</span>
            </div>
          </div>
        </div>
      </div>

      {/* referal section with link */}
      <div className="skyfxt-box">
        <div className="skyfxt-referral">
          <div className="referral-link-box">
            <p>
              <strong>Your Referral Link:</strong>
            </p>
            <p className="referral-note">
              You earn <strong>$100</strong> for each successful referral.
            </p>
            <div className="referral-link-row">
              <button
                className="copy-btn"
                onClick={() =>
                  navigator.clipboard.writeText(
                    `${window.location.origin}/account/${user?.uid}`,
                  )
                }
                title="Copy to clipboard"
              >
                <FontAwesomeIcon icon={faCopy} />
              </button>
              <code className="ref-link">
                {window.location.origin}/account/{user?.uid}
              </code>
            </div>
          </div>
        </div>
      </div>

      {recommendedPlan && (
        <div className="skyfxt-box">
          <div className="skyfxt-plan">
            <div className="plan-header">
              <span className="plan-badge">Recommended</span>
              <span className="plan-price">{recommendedPlan.price}</span>
            </div>
            <div className="plan-name">{recommendedPlan.name}</div>
            <div className="plan-stats">
              <div className="stat">
                <span className="stat-label">Duration</span>
                <span className="stat-value">{recommendedPlan.durationDisplay}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Profit</span>
                <span className="stat-value highlight">+{recommendedPlan.profit}%</span>
              </div>
              <div className="stat">
                <span className="stat-label">TPS</span>
                <span className="stat-value">{recommendedPlan.maxTps}</span>
              </div>
            </div>
            <button className="plan-btn" onClick={() => navigate(`/investments/${recommendedPlan.key}`)}>
              Invest Now
            </button>
          </div>
        </div>
      )}

{/* skyfxt balance breakdown */}
      <div className="skyfxt-box">
        <div className="skyfxt-menu">
          <div className="skyfxt-menu-title">SkyFxt Account Breakdown</div>
          <div className="skyfxt-stats">
            <div className="stat-box">
              <span className="stat-label">Trading Profit</span>
              <span className="stat-value">
                $
                {tradingProfit.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="stat-box">
              <span className="stat-label">Total Ref</span>
              <span className="stat-value">{refCount}</span>
            </div>
            {beneficiaryBalances.map((ben) => (
              <div className="stat-box" key={ben.name}>
                <span className="stat-label">{ben.name}</span>
                <span className="stat-value">
                  $
                  {ben.amount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MegaFXT;
